import { formatInt, formatMegabytes, formatMs } from '../../lib/format.ts'
import { useRolling } from '../../lib/useRolling.ts'
import type { IngestMetrics, RuntimeMetrics } from '../../lib/sse/types.ts'
import { Sparkline } from '../charts/Sparkline.tsx'
import { Metric } from '../primitives/Metric.tsx'
import { Panel } from '../primitives/Panel.tsx'

/** A minute of GC pause per minute of wall clock is the runtime, not the app. */
const GC_WARN_MS = 250
const GC_CRIT_MS = 900

/**
 * Runtime and transport.
 *
 * Memory and GC arrive as instants, which cannot answer the only question worth
 * asking about them. The sparklines are a short client-side tail — bounded, and
 * cleared on reconnect so a line is never drawn across a gap that was not
 * observed.
 */
export function RuntimePanel({
  runtime,
  ingest,
  sampleKey,
  resetKey,
  className,
}: {
  runtime: RuntimeMetrics | null
  ingest: IngestMetrics | null
  /** Changes once per `metrics` event. */
  sampleKey: string | null
  /** Changes on every snapshot. */
  resetKey: number | null
  className?: string
}) {
  const memory = useRolling(runtime?.managed_memory_mb ?? null, sampleKey, resetKey)
  const pauses = useRolling(runtime?.gc_pause_ms_last_minute ?? null, sampleKey, resetKey)

  const pause = runtime?.gc_pause_ms_last_minute ?? null
  const pauseTone =
    pause === null ? 'neutral' : pause >= GC_CRIT_MS ? 'crit' : pause >= GC_WARN_MS ? 'warn' : 'neutral'

  return (
    <Panel title="Infraestrutura" className={className}>
      <div className="flex h-full flex-col gap-3">
        <Metric
          label="conexões gRPC ativas"
          value={formatInt(ingest?.active_connections)}
          size="lg"
        />

        <div className="border-t border-line-subtle pt-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-micro uppercase tracking-label text-fg-muted">
              memória gerenciada
            </span>
            <span className="live-value font-mono text-sm text-fg">
              {formatMegabytes(runtime?.managed_memory_mb)}
            </span>
          </div>
          <Sparkline
            values={memory}
            label={`Memória gerenciada nos últimos ${memory.length} ticks`}
          />
        </div>

        <div className="border-t border-line-subtle pt-2">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-micro uppercase tracking-label text-fg-muted">
              pausas de GC / min
            </span>
            <span
              className={`live-value font-mono text-sm ${
                pauseTone === 'crit' ? 'text-crit' : pauseTone === 'warn' ? 'text-warn' : 'text-fg'
              }`}
            >
              {formatMs(pause)}
            </span>
          </div>
          <Sparkline
            values={pauses}
            zeroBased
            label={`Pausas de GC nos últimos ${pauses.length} ticks`}
          />
        </div>

        <div className="mt-auto border-t border-line-subtle pt-2">
          <Metric
            label="coletas gen2 / min"
            value={formatInt(runtime?.gen2_collections_last_minute)}
          />
        </div>
      </div>
    </Panel>
  )
}
